import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { filter, finalize } from 'rxjs/operators';
import { CurrentContextService } from '../../../core/context/current-context.service';
import {
  LiveUpdateEventTypes,
  growthSearchOpsTopic,
  liveStatusTranslationKey
} from '../../../core/live-updates/live-update.models';
import { LiveUpdateService } from '../../../core/live-updates/live-update.service';
import { LocalizationService } from '../../../core/localization/localization.service';
import { AinFormFieldComponent } from '../../../shared/design-system/form-field.component';
import { InfoHintComponent } from '../../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../../shared/design-system/page.component';
import { AinStateComponent } from '../../../shared/design-system/state.component';
import { StructuredJsonDisplayComponent } from '../../../shared/design-system/structured-json-display.component';
import {
  SearchIssueDto,
  SearchPropertyListItemDto,
  searchOpsEvidenceFields
} from './growth-search-ops.models';
import { GrowthSearchOpsService } from './growth-search-ops.service';

@Component({
  standalone: true,
  selector: 'ain-growth-search-ops-issues',
  imports: [
    AinPageComponent,
    AinStateComponent,
    InfoHintComponent,
    AinFormFieldComponent,
    FormsModule,
    ButtonModule,
    SelectModule,
    TableModule,
    RouterLink,
    StructuredJsonDisplayComponent
  ],
  template: `
    <ain-page
      [eyebrow]="t('growth.eyebrow')"
      [title]="t('growth.searchops.issues.title')"
      [description]="t('growth.searchops.issues.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'growth.searchops.issues.help.title'"
        [descriptionKey]="'growth.searchops.issues.help.description'"
        [bulletKeys]="[
          'growth.searchops.issues.help.bullets.crawl',
          'growth.searchops.issues.help.bullets.task',
          'growth.searchops.issues.help.bullets.live'
        ]" />

      <div class="toolbar">
        <button pButton type="button" severity="secondary" [label]="t('common.refresh')" (click)="load(true)"></button>
        <a pButton severity="secondary" [routerLink]="['/growth/search-ops/urls']" [queryParams]="propertyQuery()" [label]="t('growth.searchops.workHome.actions.urls')"></a>
        <a pButton severity="secondary" [routerLink]="['/growth/search-ops/backlog']" [queryParams]="propertyQuery()" [label]="t('growth.searchops.workHome.actions.backlog')"></a>
        <span class="live-badge">{{ t(liveStatusKey(liveUpdateService.connectionState())) }}</span>
      </div>

      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (error()) {
        <ain-state type="error" [title]="t('common.error')" [message]="error()" />
      } @else if (properties().length === 0) {
        <ain-state type="empty" [title]="t('growth.searchops.workHome.empty.title')" [message]="t('growth.searchops.workHome.empty.message')" />
      } @else {
        <section class="card">
          <ain-form-field [label]="t('growth.searchops.fields.property')">
            <p-select
              [options]="propertyOptions()"
              optionLabel="label"
              optionValue="value"
              [(ngModel)]="selectedPropertyId"
              appendTo="body"
              (onChange)="onPropertyChange($event.value)" />
          </ain-form-field>
        </section>

        <section class="card">
          <div class="section-title">
            <h3>{{ t('growth.searchops.issues.sections.board') }}</h3>
            <ain-info-hint
              [titleKey]="'growth.searchops.issues.boardHelp.title'"
              [descriptionKey]="'growth.searchops.issues.boardHelp.description'" />
          </div>
          <p-table [value]="issues()" [paginator]="true" [rows]="20" dataKey="id" [(selection)]="selectedIssue" selectionMode="single">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('growth.searchops.fields.severity') }}</th>
                <th>{{ t('growth.searchops.fields.category') }}</th>
                <th>{{ t('growth.searchops.fields.url') }}</th>
                <th>{{ t('growth.searchops.fields.status') }}</th>
                <th>{{ t('growth.searchops.fields.actions') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr [pSelectableRow]="row">
                <td>{{ t('growth.searchops.issueSeverity.' + row.severity) }}</td>
                <td>{{ t('growth.searchops.issueCategory.' + row.category) }}</td>
                <td class="wrap-url">{{ row.url || '—' }}</td>
                <td>{{ t('growth.searchops.issueStatus.' + row.status) }}</td>
                <td class="actions">
                  <button
                    pButton
                    type="button"
                    size="small"
                    [disabled]="busy() || !!row.taskId"
                    [label]="t('growth.searchops.issues.actions.createTask')"
                    (click)="createTask(row)"></button>
                  @if (row.taskId) {
                    <a pButton size="small" severity="secondary" [routerLink]="['/growth/search-ops/backlog']" [queryParams]="{ propertyId: selectedPropertyId, taskId: row.taskId }" [label]="t('growth.searchops.issues.actions.openTask')"></a>
                  }
                </td>
              </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
              <tr>
                <td colspan="5">{{ t('growth.searchops.issues.empty') }}</td>
              </tr>
            </ng-template>
          </p-table>
        </section>

        @if (selectedIssue; as issue) {
          <section class="card">
            <div class="section-title">
              <h3>{{ t('growth.searchops.issues.sections.evidence') }}</h3>
              <ain-info-hint
                [titleKey]="'growth.searchops.issues.evidenceHelp.title'"
                [descriptionKey]="'growth.searchops.issues.evidenceHelp.description'" />
            </div>
            <ain-structured-json-display [fields]="evidenceFields" [payloadJson]="issue.evidenceJson" />
            @if (issue.seoProfileId) {
              <p class="summary">
                <a [routerLink]="['/growth/seo/profiles', issue.seoProfileId]">{{ t('growth.searchops.urls.openProfile') }}</a>
              </p>
            }
          </section>
        }
      }
    </ain-page>
  `,
  styles: [`
    .toolbar { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; margin-bottom: 12px; }
    .card { background: var(--ain-color-surface, #fff); padding: 16px; border-radius: 12px; margin-bottom: 12px; }
    .section-title { display: flex; align-items: center; gap: 8px; margin-bottom: 8px; }
    .section-title h3 { margin: 0; font-size: 1rem; }
    .live-badge { font-size: 0.85rem; color: #64748b; }
    .actions { display: flex; flex-wrap: wrap; gap: 6px; }
    .wrap-url { overflow-wrap: anywhere; }
    .summary { margin-top: 8px; }
  `]
})
export class GrowthSearchOpsIssuesComponent implements OnInit {
  private readonly api = inject(GrowthSearchOpsService);
  private readonly localization = inject(LocalizationService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly context = inject(CurrentContextService);
  private readonly destroyRef = inject(DestroyRef);
  readonly liveUpdateService = inject(LiveUpdateService);
  readonly liveStatusKey = liveStatusTranslationKey;
  readonly evidenceFields = searchOpsEvidenceFields;

  readonly loading = signal(false);
  readonly busy = signal(false);
  readonly error = signal('');
  readonly properties = signal<SearchPropertyListItemDto[]>([]);
  readonly issues = signal<SearchIssueDto[]>([]);
  selectedPropertyId: string | null = null;
  selectedIssue: SearchIssueDto | null = null;
  private subscribedTopic: string | null = null;

  ngOnInit(): void {
    this.liveUpdateService.events$
      .pipe(filter(e => e.eventType === LiveUpdateEventTypes.growthSearchOpsChanged), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.load(false));
    this.destroyRef.onDestroy(() => {
      if (this.subscribedTopic) {
        void this.liveUpdateService.unsubscribe(this.subscribedTopic);
        this.subscribedTopic = null;
      }
    });
    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      this.selectedPropertyId = params.get('propertyId');
      this.load(true);
    });
    void this.setupLiveSubscription();
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  propertyOptions() {
    return this.properties().map(item => ({ value: item.id, label: `${item.name} (${item.primaryDomain})` }));
  }

  propertyQuery() {
    return this.selectedPropertyId ? { propertyId: this.selectedPropertyId } : {};
  }

  onPropertyChange(propertyId: string): void {
    void this.router.navigate([], { relativeTo: this.route, queryParams: { propertyId }, queryParamsHandling: 'merge' });
  }

  load(showSpinner: boolean): void {
    if (showSpinner) {
      this.loading.set(true);
    }
    this.error.set('');
    this.api.listProperties().pipe(finalize(() => this.loading.set(false))).subscribe({
      next: properties => {
        this.properties.set(properties);
        if (!this.selectedPropertyId && properties.length > 0) {
          this.selectedPropertyId = properties[0].id;
        }
        this.loadBoard();
      },
      error: err => this.error.set(err?.error?.error ?? this.t('growth.searchops.messages.loadFailed'))
    });
  }

  createTask(row: SearchIssueDto): void {
    this.busy.set(true);
    this.api.createTaskFromIssue(row.id).pipe(finalize(() => this.busy.set(false))).subscribe({
      next: () => this.loadBoard(),
      error: err => this.error.set(err?.error?.error ?? this.t('growth.searchops.messages.saveFailed'))
    });
  }

  private loadBoard(): void {
    if (!this.selectedPropertyId) {
      this.issues.set([]);
      this.selectedIssue = null;
      return;
    }
    this.api.listIssues(this.selectedPropertyId).subscribe({
      next: items => {
        this.issues.set(items);
        if (this.selectedIssue) {
          this.selectedIssue = items.find(item => item.id === this.selectedIssue?.id) ?? null;
        }
      },
      error: err => this.error.set(err?.error?.error ?? this.t('growth.searchops.messages.loadFailed'))
    });
  }

  private async setupLiveSubscription(): Promise<void> {
    const tenantId = this.context.context()?.tenantId;
    if (!tenantId) {
      return;
    }
    this.subscribedTopic = growthSearchOpsTopic(tenantId);
    await this.liveUpdateService.subscribe(this.subscribedTopic);
  }
}
